import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { validateInvite, completeInvite } from "../../../services/academetrics";
import styles from "./inviteComplete.module.css";

const InviteComplete = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("loading"); // loading | valid | invalid | success
  const [inviteInfo, setInviteInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setErrorMessage("Link inválido. Verifique o endereço do e-mail.");
      return;
    }

    const validate = async () => {
      try {
        const data = await validateInvite(token);
        if (data.message) {
          setStatus("invalid");
          setErrorMessage(data.message || "Este link não é válido ou já foi utilizado.");
          return;
        }
        setInviteInfo({
          email: data.email,
          mantenedoraName: data.mantenedoraName,
          institutionName: data.institutionName
        });
        setStatus("valid");
      } catch (err) {
        setStatus("invalid");
        setErrorMessage("Não foi possível validar o link. Tente novamente mais tarde.");
      }
    };

    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!firstName.trim() || !lastName.trim() || !password) {
      setSubmitError("Preencha nome, sobrenome e senha.");
      return;
    }
    if (password.length < 6) {
      setSubmitError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    setIsSubmitting(true);
    try {
      await completeInvite({
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        phoneNumber: phoneNumber.trim() || undefined
      });
      setStatus("success");
    } catch (err) {
      const messages = err.response?.data;
      setSubmitError(
        Array.isArray(messages) ? messages.join(" ") : messages?.message || "Erro ao concluir cadastro. Tente novamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className={styles.wrapper}>
        <p className={styles.loading}>Validando seu link...</p>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className={styles.wrapper}>
        <h1 className={styles.title}>Link inválido ou expirado</h1>
        <p className={styles.describe}>{errorMessage}</p>
        <p className={styles.linkToLogin}>
          <Link to="/auth/login">Ir para o login</Link>
        </p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className={styles.wrapper}>
        <h1 className={styles.title}>Cadastro concluído</h1>
        <p className={styles.describe}>
          Sua conta foi ativada. Faça login com seu e-mail e senha.
        </p>
        <p className={styles.linkToLogin}>
          <Link to="/auth/login">Fazer login</Link>
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.title}>Concluir cadastro</h1>
      <p className={styles.describe}>
        Preencha seus dados para finalizar o cadastro e acessar o sistema.
      </p>

      <div className={styles.contextCard}>
        <div className={styles.contextLabel}>Você está sendo vinculado a</div>
        <div className={styles.contextValue}>
          {inviteInfo?.mantenedoraName || "—"}
        </div>
        {inviteInfo?.institutionName && (
          <div className={`${styles.contextValue} ${styles.institution}`}>
            Instituição: {inviteInfo.institutionName}
          </div>
        )}
        {inviteInfo?.email && (
          <div className={styles.contextValue} style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e6eff7" }}>
            E-mail: {inviteInfo.email}
          </div>
        )}
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        {submitError && <div className={styles.errorMessage}>{submitError}</div>}
        <div className={styles.inputContainer}>
          <input
            type="text"
            placeholder="Nome"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={submitError && !firstName.trim() ? styles.inputError : ""}
            autoComplete="given-name"
          />
        </div>
        <div className={styles.inputContainer}>
          <input
            type="text"
            placeholder="Sobrenome"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={submitError && !lastName.trim() ? styles.inputError : ""}
            autoComplete="family-name"
          />
        </div>
        <div className={styles.inputContainer}>
          <input
            type="password"
            placeholder="Senha (mín. 6 caracteres)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={submitError && !password ? styles.inputError : ""}
            autoComplete="new-password"
          />
        </div>
        <div className={styles.inputContainer}>
          <input
            type="tel"
            placeholder="Telefone (opcional)"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            autoComplete="tel"
          />
        </div>
        <button type="submit" className={styles.button} disabled={isSubmitting}>
          {isSubmitting ? "Salvando..." : "Concluir cadastro"}
        </button>
      </form>

      <p className={styles.linkToLogin}>
        Já tem conta? <Link to="/auth/login">Fazer login</Link>
      </p>
    </div>
  );
};

export default InviteComplete;
